import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { GraduationCap, Plus, Users, Award, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

const ROLE_CHIP = {
    HOUSEKEEPER: "bg-emerald-50 text-emerald-700 border-emerald-200",
    SECURITY: "bg-amber-50 text-amber-700 border-amber-200",
    RECEPTIONIST: "bg-indigo-50 text-indigo-700 border-indigo-200",
    OTHER: "bg-gray-100 text-gray-700 border-gray-200",
    ALL: "bg-[#1E3355]/10 text-[#1E3355] border-[#1E3355]/20",
};

export default function TrainingListPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isHO = user?.role === "HO_ADMIN";

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/courses"); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Learning</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><GraduationCap className="w-7 h-7" /> {t(user, "training_title")}</h1>
                    <p className="text-sm text-gray-500 mt-1">{t(user, "training_subtitle")}</p>
                </div>
                {isHO && (
                    <Link to="/admin/courses/new" className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="new-course-btn">
                        <Plus className="w-4 h-4" /> {t(user, "training_new_course")}
                    </Link>
                )}
            </div>

            {loading ? <div className="mt-6 h-40 rounded-xl bg-white animate-pulse" /> : (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="courses-grid">
                    {rows.map((c) => (
                        <div key={c.id} className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid={`course-card-${c.id}`}>
                            <div className="p-4">
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ROLE_CHIP[c.target_role] || ROLE_CHIP.OTHER}`}>{c.target_role}</span>
                                    <span className="text-[11px] text-gray-500">{c.slides?.length || 0} slides · {c.quiz?.length || 0} questions · pass {c.pass_mark}/5</span>
                                    {!c.published && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 border border-gray-200">Draft</span>}
                                </div>
                                <div className="font-display font-semibold text-[#1E3355]">{c.title}</div>
                                {c.description && <div className="text-xs text-gray-600 mt-1">{c.description}</div>}
                                <div className="mt-3 flex items-center gap-1.5">
                                    <Link to={`/training/${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold bg-[#1E3355] hover:bg-[#15243C] text-white px-3 py-1.5 rounded-md" data-testid={`course-take-${c.id}`}>
                                        <Users className="w-3.5 h-3.5" /> {t(user, "training_take")} <ChevronRight className="w-3.5 h-3.5" />
                                    </Link>
                                    {isHO && (
                                        <Link to={`/admin/courses/${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:text-[#1E3355] px-3 py-1.5 rounded-md" data-testid={`course-edit-${c.id}`}>Edit</Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {!rows.length && <div className="col-span-2 rounded-xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500">No courses yet.</div>}
                </div>
            )}
        </div>
    );
}
